import React, { useState } from 'react';
import api from '../services/api';
import { useAccounts } from '../context/AccountContext';
import ScalingModesInfoModal from './ScalingModesInfoModal';

// Suffixes the copier-side bridge strips by default when "Auto-strip broker
// suffixes" is on (mirrors _SYMBOL_SUFFIXES in mt5-base/bridge.py — keep in
// sync). Shown to the copier so they know what's already covered before
// adding their own.
const BUILTIN_SUFFIXES = ['c', 'm', '.raw', '.r', '.pro', '!', '.cash', '.std', '_'];

// `trader` is a TRADER ACCOUNT from the browse list (its `id` is the account
// id). `existing` is a relationship from My Traders when editing.
export default function ScalingModal({ trader, existing, onClose, onSave }) {
  // An existing relationship is bound by the terms snapshotted at follow time,
  // which may differ from what the trader advertises today — show the copier
  // what they are actually on, not the current sticker price.
  // PROFIT-SHARE BILLING — TEMPORARILY DISABLED (2026-08-19). Pinning this to
  // null is the whole switch: the disclosure block and its acceptance checkbox
  // below both render on `fee && !fee.free`, and the submit gate reads the same
  // expression, so nothing fee-related shows or blocks a follow. The JSX is
  // left in place for future dev. To restore, uncomment the line below.
  // See the banner in worker.js.
  const fee = null;
  // const fee = existing?.feeTerms || trader?.feeTerms || null;
  const { accounts, selectedId } = useAccounts();
  // Which of MY accounts copies this trader. Fixed when editing (a
  // relationship's copier account can't move — its open positions live there).
  // For a new follow, default to the account currently in the switcher, else
  // the only verified one.
  const verified = accounts.filter(a => a.status === 'verified');
  const [copierAccountId, setCopierAccountId] = useState(() => {
    if (existing) return existing.copierAccountId || '';
    if (selectedId && verified.some(a => a.id === selectedId)) return selectedId;
    return verified.length === 1 ? verified[0].id : '';
  });
  const [form, setForm] = useState({
    scalingMode:    existing?.scalingMode    || 'ratio',
    scalingValue:   existing?.scalingValue   || 1.0,
    riskMultiplier: existing?.riskMultiplier || 1.0,
    maxDrawdown:    existing?.maxDrawdown    ?? 0,
    maxOpenTrades:  existing?.maxOpenTrades  ?? 0,
    minLotSize:     existing?.minLotSize     || 0.01,
    maxLotSize:     existing?.maxLotSize ?? 0,   // 0 = no cap; ?? not || so a stored 0 survives
    allowedSymbols: existing?.allowedSymbols || 'all',
    blockedSymbols: (existing?.blockedSymbols || []).join(','),
    reverseMode:    existing?.reverseMode    || false,
    allowSymbolSuffixStrip: existing?.allowSymbolSuffixStrip || false,
    mirrorDowntimeOpens: existing?.mirrorDowntimeOpens !== false, // default ON

    customSuffixes: (existing?.customSuffixes || []).join(', '),
    symbolMap:      (existing?.symbolMap || []).map(m => ({ from: m.from, to: m.to })),
    suffixReplacements: (existing?.suffixReplacements || []).map(m => ({ from: m.from, to: m.to })),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [showInfo, setShowInfo] = useState(false);
  // Explicit consent to the profit-share terms. Only gates a NEW follow: an
  // existing relationship is already bound by the terms snapshotted when it was
  // created, and re-consenting to them on every settings edit would train the
  // user to click past the one screen that matters.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function addMapping() {
    setForm(prev => ({ ...prev, symbolMap: [...prev.symbolMap, { from: '', to: '' }] }));
  }
  function updateMapping(i, field, value) {
    setForm(prev => ({
      ...prev,
      symbolMap: prev.symbolMap.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)),
    }));
  }
  function removeMapping(i) {
    setForm(prev => ({ ...prev, symbolMap: prev.symbolMap.filter((_, idx) => idx !== i) }));
  }

  function addSuffixRepl() {
    setForm(prev => ({ ...prev, suffixReplacements: [...prev.suffixReplacements, { from: '', to: '' }] }));
  }
  function updateSuffixRepl(i, field, value) {
    setForm(prev => ({
      ...prev,
      suffixReplacements: prev.suffixReplacements.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)),
    }));
  }
  function removeSuffixRepl(i) {
    setForm(prev => ({ ...prev, suffixReplacements: prev.suffixReplacements.filter((_, idx) => idx !== i) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Guard the numerics: a cleared input parses to NaN, which JSON-serializes
    // to null and lands in the DB as a broken scaling config.
    const scalingValue   = parseFloat(form.scalingValue);
    const riskMultiplier = parseFloat(form.riskMultiplier);
    const minLotSize     = parseFloat(form.minLotSize);
    const maxLotSize     = parseFloat(form.maxLotSize);
    if (!Number.isFinite(riskMultiplier) || riskMultiplier < 0.1 || riskMultiplier > 100) {
      setError('Risk multiplier must be between 0.1 and 100.'); return;
    }
    if (form.scalingMode === 'fixed_lot' && (!Number.isFinite(scalingValue) || scalingValue <= 0)) {
      setError('Enter a fixed lot size greater than 0.'); return;
    }
    if (!Number.isFinite(minLotSize) || minLotSize <= 0) {
      setError('Min lot size must be a number greater than 0.'); return;
    }
    // Max lot size is optional: blank or 0 means no per-position cap.
    const maxRaw = String(form.maxLotSize ?? '').trim();
    const maxDisabled = maxRaw === '' || maxLotSize === 0;
    if (!maxDisabled && (!Number.isFinite(maxLotSize) || maxLotSize < 0)) {
      setError('Max lot size must be a number greater than 0, or 0 for no cap.'); return;
    }
    if (!maxDisabled && minLotSize > maxLotSize) {
      setError('Min lot size cannot be greater than max lot size.'); return;
    }

    if (!existing && !copierAccountId) {
      setError('Choose which of your MT5 accounts should copy this trader.'); return;
    }
    if (!existing && fee && !fee.free && !acceptedTerms) {
      setError('Please confirm you accept the profit-share terms before following.'); return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        scalingValue:   Number.isFinite(scalingValue) ? scalingValue : 1.0,
        riskMultiplier,
        maxDrawdown:    parseFloat(form.maxDrawdown) || 0,
        maxOpenTrades:  parseInt(form.maxOpenTrades, 10) || 0,
        minLotSize,
        maxLotSize:     maxDisabled ? 0 : maxLotSize,
        blockedSymbols: form.blockedSymbols
          ? form.blockedSymbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
          : [],
        customSuffixes: form.customSuffixes
          ? form.customSuffixes.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        symbolMap: (form.symbolMap || [])
          .map(m => ({ from: (m.from || '').trim().toUpperCase(), to: (m.to || '').trim() }))
          .filter(m => m.from && m.to),
        // Suffix case is preserved (MT5 symbol suffixes are case-sensitive: '.v', 'c').
        suffixReplacements: (form.suffixReplacements || [])
          .map(m => ({ from: (m.from || '').trim(), to: (m.to || '').trim() }))
          .filter(m => m.from && m.to),
      };

      if (existing) {
        // Editing addresses the RELATIONSHIP: with multi-account, "my
        // relationship with trader X" is no longer unique.
        await api.put(`/copier/relationships/${existing.id}`, payload);
      } else {
        const traderAccountId = trader.id || trader._id;
        await api.post(`/copier/follow/${traderAccountId}`, {
          ...payload, copierAccountId,
        });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{
        background: 'rgba(0,0,0,0.7)',
        // Bootstrap's default .modal has overflow:hidden, so a tall
        // form clips the footer buttons on mobile (Cancel / Update
        // become unreachable). Make the whole modal page-scroll
        // instead — the dialog scrolls with the viewport.
        overflowY: 'auto',
      }}
    >
      <div className="modal-dialog modal-lg" style={{ marginTop: 16, marginBottom: 16 }}>
        <div className="modal-content" style={{ background: '#27293d', color: '#fff' }}>
          <div className="modal-header border-secondary">
            <h5 className="modal-title">
              {existing ? 'Edit Settings' : 'Follow Trader'}: {trader?.username || trader?.email}
              {trader?.accountLabel ? ` · ${trader.accountLabel}` : ''}
            </h5>
            <button className="close text-white" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {fee && !fee.free && (
              <div
                className="mb-3 p-3"
                style={{ background: '#1e1e2f', borderLeft: '3px solid #F5B301', borderRadius: 4 }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  You keep {fee.copierPct}% of the profit this trader makes you
                </div>
                <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {fee.feePct}% of each day&apos;s net profit is charged as a performance fee
                  {' '}({fee.traderPct}% to the trader, {fee.platformPct}% to the platform).
                  Losing days are charged nothing, and losses carry forward &mdash; you are only
                  ever charged on profit above your previous high&#8209;water mark. Fees are
                  calculated after commission and swap, and settled daily.
                </div>
                {!existing && (
                  <>
                    <ul
                      className="text-muted mb-2"
                      style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18, marginTop: 10 }}
                    >
                      <li>If an invoice goes unpaid, new copying from this trader pauses.</li>
                      <li>
                        While unpaid, this trader&apos;s exits stop being mirrored &mdash; positions
                        opened for you stay open <strong>at your own risk</strong> until you settle
                        or close them yourself.
                      </li>
                      <li>
                        You can always close your own positions from your dashboard, whether or not
                        a fee is outstanding.
                      </li>
                    </ul>
                    <label className="d-flex align-items-start" style={{ gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={e => setAcceptedTerms(e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: 13 }}>
                        I understand and accept these terms.
                      </span>
                    </label>
                  </>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Which of the copier's MT5 accounts mirrors this trader.
                    Shown as read-only when editing: moving a live relationship
                    to another account would strand its open positions on the
                    old one. */}
                {existing ? (
                  existing.copierAccountLabel ? (
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>Copying into</label>
                        <input className="form-control" value={existing.copierAccountLabel} disabled />
                        <small className="text-muted">
                          To copy this trader from another of your accounts, follow them
                          again from that account.
                        </small>
                      </div>
                    </div>
                  ) : null
                ) : verified.length > 1 ? (
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Copy into which MT5 account?</label>
                      <select
                        className="form-control"
                        value={copierAccountId}
                        onChange={e => setCopierAccountId(e.target.value)}
                      >
                        <option value="">Select an account…</option>
                        {verified.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.displayLabel} — {a.server} #{a.accountNumber}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        Trades are mirrored into this account only. You can follow the
                        same trader from another account separately.
                      </small>
                    </div>
                  </div>
                ) : null}
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="d-flex justify-content-between align-items-center">
                      <span>Scaling Mode</span>
                      <button type="button"
                        className="btn btn-link btn-sm p-0"
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => setShowInfo(true)}>
                        How do these work?
                      </button>
                    </label>
                    <select name="scalingMode" className="form-control" value={form.scalingMode} onChange={handleChange}>
                      <option value="ratio">Auto Ratio (equity-based)</option>
                      <option value="mirror">Mirror (same lots as trader)</option>
                      <option value="fixed_lot">Fixed Lot Size</option>
                      <option value="equity_percent">Equity Percent</option>
                    </select>
                  </div>
                </div>
                {form.scalingMode === 'fixed_lot' && (
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Fixed Lot Size</label>
                      <input type="number" name="scalingValue" className="form-control"
                        step="0.01" min="0.01" value={form.scalingValue} onChange={handleChange} />
                    </div>
                  </div>
                )}
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Risk Multiplier</label>
                    <input type="number" name="riskMultiplier" className="form-control"
                      min="0.1" max="100" step="0.1" value={form.riskMultiplier} onChange={handleChange} />
                    <div className="text-muted small mt-1">
                      0.1x-100x. 1.0x is normal; above 3x multiplies your exposure well beyond
                      the trader's. Set a Max Lot Size below to put a hard cap on any resulting
                      position — it is 0 (no cap) by default.
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Min Lot Size</label>
                    <input type="number" name="minLotSize" className="form-control"
                      step="0.01" min="0.01" value={form.minLotSize} onChange={handleChange} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      Max Lot Size — 0 disables
                    </label>
                    <input type="number" name="maxLotSize" className="form-control"
                      step="0.01" min="0" value={form.maxLotSize} onChange={handleChange}
                      placeholder="e.g. 5" />
                    <small className="text-muted">
                      Caps each copied position on its own, never your total across
                      open positions. While set it is also the limit a round-up to
                      your broker's minimum volume may not cross — a copy needing
                      more is skipped rather than exceeding it. Leave at 0 for no cap.
                    </small>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group">
                    <label>
                      Max Drawdown for this trader (%) — 0 disables
                    </label>
                    <input type="number" name="maxDrawdown" className="form-control"
                      step="1" min="0" max="100" value={form.maxDrawdown} onChange={handleChange}
                      placeholder="e.g. 20" />
                    <small className="text-muted">
                      Measured from your peak equity since you started following
                      (whichever is highest of your follow-time equity, your
                      peak since, and now), and adjusted for deposits and
                      withdrawals so cashing out profits won't trip it. This
                      relationship auto-pauses if your equity falls by this %
                      from that baseline.
                    </small>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group">
                    <label>
                      Max Open Trades for this trader — 0 disables
                    </label>
                    <input type="number" name="maxOpenTrades" className="form-control"
                      step="1" min="0" max="500" value={form.maxOpenTrades} onChange={handleChange}
                      placeholder="e.g. 50" />
                    <small className="text-muted">
                      Cap on simultaneously-open copied positions from this trader.
                      New signals are skipped when this many of their trades are
                      already open on your account. Leave at 0 for no cap.
                    </small>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Symbols not to trade in your account (comma-separated)</label>
                    <input type="text" name="blockedSymbols" className="form-control"
                      placeholder="e.g. XAUUSD,BTCUSD" value={form.blockedSymbols} onChange={handleChange} />
                    <small className="text-muted">
                      Trades on these symbols are never copied to your account. Match
                      is by instrument and ignores broker suffixes — blocking
                      <code> BTCUSD </code> also blocks a cent trader's
                      <code> BTCUSDc</code>.
                    </small>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-check">
                    <input type="checkbox" name="reverseMode" id="reverseMode"
                      className="form-check-input" checked={form.reverseMode} onChange={handleChange} />
                    <label className="form-check-label" htmlFor="reverseMode">
                      Reverse Mode (copy BUY as SELL and vice versa)
                    </label>
                  </div>
                </div>
                <div className="col-md-12" style={{marginTop:12}}>
                  <div className="form-check">
                    <input type="checkbox" name="mirrorDowntimeOpens" id="mirrorDowntimeOpens"
                      className="form-check-input"
                      checked={form.mirrorDowntimeOpens}
                      onChange={handleChange} />
                    <label className="form-check-label" htmlFor="mirrorDowntimeOpens">
                      Copy trades reconciled after downtime
                    </label>
                    <div className="text-muted small" style={{marginTop:4}}>
                      If a brief outage interrupts monitoring, trades the trader
                      opened during it are copied once the connection recovers —
                      at the <strong>current market price</strong>, which can
                      differ from the trader's entry. Only recently-opened
                      trades are copied this way; older positions are never
                      pulled in. Untick to skip these late entries entirely.
                    </div>
                  </div>
                </div>
                <div className="col-md-12" style={{marginTop:12}}>
                  <div className="form-check">
                    <input type="checkbox" name="allowSymbolSuffixStrip" id="allowSymbolSuffixStrip"
                      className="form-check-input"
                      checked={form.allowSymbolSuffixStrip}
                      onChange={handleChange} />
                    <label className="form-check-label" htmlFor="allowSymbolSuffixStrip">
                      Auto-strip broker suffixes (e.g. BTCUSDc → BTCUSD)
                    </label>
                    <div className="text-muted small" style={{marginTop:4}}>
                      Enable only if you understand that cent and standard
                      accounts can have <strong>different contract sizes</strong>.
                      An Exness "BTCUSDc" lot is typically 1/100 the size of
                      a standard "BTCUSD" lot — a 1.0 lot trade copied
                      verbatim could be 100× larger than intended. Off by
                      default: trades for unmatched symbols will fail with
                      a clear error instead.
                    </div>
                  </div>

                  {form.allowSymbolSuffixStrip && (
                    <div style={{ marginTop: 10, paddingLeft: 24 }}>
                      <div className="text-muted small" style={{ marginBottom: 4 }}>
                        Suffixes stripped automatically:
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        {BUILTIN_SUFFIXES.map(s => (
                          <span key={s} className="badge badge-secondary mr-1"
                            style={{ fontFamily: 'monospace' }}>{s}</span>
                        ))}
                      </div>
                      <div className="form-group">
                        <label className="small">
                          Add extra suffixes to strip (comma-separated)
                        </label>
                        <input type="text" name="customSuffixes" className="form-control"
                          placeholder="e.g. .ecn, .z, x"
                          value={form.customSuffixes} onChange={handleChange} />
                        <small className="text-muted">
                          Only used if your broker's symbol isn't matched by the
                          built-in list above. Same contract-size caveat applies.
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-md-12" style={{ marginTop: 12 }}>
                  <label>Symbol mappings</label>
                  <div className="text-muted small" style={{ marginBottom: 8 }}>
                    Map a trader's symbol to the exact name your broker uses
                    (e.g. <code>BTCUSD → XBTUSD</code>). Applied before any
                    suffix logic and used even when auto-strip is off, so use
                    this when the names differ in more than a suffix.
                  </div>
                  {form.symbolMap.map((m, i) => (
                    <div className="d-flex align-items-center mb-2" key={i}>
                      <input type="text" className="form-control mr-2"
                        placeholder="Trader symbol (e.g. BTCUSD)"
                        value={m.from}
                        onChange={e => updateMapping(i, 'from', e.target.value)} />
                      <span className="mr-2">→</span>
                      <input type="text" className="form-control mr-2"
                        placeholder="Your broker symbol (e.g. XBTUSD)"
                        value={m.to}
                        onChange={e => updateMapping(i, 'to', e.target.value)} />
                      <button type="button" className="btn btn-danger btn-sm"
                        onClick={() => removeMapping(i)}>&times;</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={addMapping}>+ Add mapping</button>
                </div>

                <div className="col-md-12" style={{ marginTop: 12 }}>
                  <label>Replace broker suffixes</label>
                  <div className="text-muted small" style={{ marginBottom: 8 }}>
                    Rewrite a trailing suffix to the one your broker uses
                    (e.g. <code>.v → c</code> maps <code>XAUUSD.v</code> to{' '}
                    <code>XAUUSDc</code>). Applies to every symbol ending in
                    that suffix — use this for cross-broker copying where only
                    the suffix differs. Applied before suffix-strip and even
                    when auto-strip is off. Suffixes are case-sensitive.
                    <strong> Same contract-size caveat</strong> as auto-strip:
                    cent and standard accounts can differ in lot size.
                  </div>
                  {form.suffixReplacements.map((m, i) => (
                    <div className="d-flex align-items-center mb-2" key={i}>
                      <input type="text" className="form-control mr-2"
                        placeholder="Trader suffix (e.g. .v)"
                        value={m.from}
                        onChange={e => updateSuffixRepl(i, 'from', e.target.value)} />
                      <span className="mr-2">→</span>
                      <input type="text" className="form-control mr-2"
                        placeholder="Your broker suffix (e.g. c)"
                        value={m.to}
                        onChange={e => updateSuffixRepl(i, 'to', e.target.value)} />
                      <button type="button" className="btn btn-danger btn-sm"
                        onClick={() => removeSuffixRepl(i)}>&times;</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={addSuffixRepl}>+ Add suffix replacement</button>
                </div>
              </div>
              <div className="modal-footer border-secondary px-0">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : existing ? 'Update Settings' : 'Start Following'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    {showInfo && <ScalingModesInfoModal onClose={() => setShowInfo(false)} />}
    </>
  );
}
